import { useEffect, useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Platform, StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  BodyText,
  Button,
  HighlightWords,
  HStack,
  Modal,
  theme,
  useResponsive,
} from '@sg/garnish';
import { IngredientKind } from '@sg/graphql-schema';

import { type CustomizationMachineTypes } from '../../../machine';
import { CustomizationModalContentContainer } from '../CustomizationModalContentContainer';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationEncourageChangesModal = (props: Props) => {
  const {
    isVisible,
    entry,
    onAddMore,
    onContinue,
    onShow,
    onRequestClose,
    size,
  } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  const formattedGroupName = entry.groupDisplayName.toLowerCase();
  const accessibilityLabel = formatMessage(messages.modalLabel, {
    group: formattedGroupName,
  });

  const isBasesGroup = entry.modificationIngredientKind === IngredientKind.Base;

  // ─── Messages ────────────────────────────────────────────────────────

  const heading = useMemo(() => {
    if (entry.currentSelection === 0) {
      return formatMessage(messages.headingNoSelection, {
        group: formattedGroupName,
      });
    }

    if (isBasesGroup) {
      return formatMessage(messages.headingBases, {
        count: entry.currentSelection,
        group: formattedGroupName,
      });
    }

    return formatMessage(messages.heading, {
      count: entry.currentSelection,
      group: formattedGroupName,
    });
  }, [entry.currentSelection, isBasesGroup, formattedGroupName, formatMessage]);

  const headingHighlights = useMemo(() => {
    const currentSelection = `${entry.currentSelection}`;

    if (isBasesGroup) {
      return [currentSelection, formatMessage(messages.base)];
    }

    return [currentSelection, formattedGroupName];
  }, [isBasesGroup, entry.currentSelection, formattedGroupName, formatMessage]);

  const body = useMemo(() => {
    if (isBasesGroup) {
      return formatMessage(messages.bodyBases, {
        group: formattedGroupName,
      });
    }

    return formatMessage(messages.body, {
      group: formattedGroupName,
    });
  }, [isBasesGroup, formattedGroupName, formatMessage]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const ctaStyles = size === 'lg' ? styles.ctaLG : match([styles.ctaXS, undefined]); // prettier-ignore

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isVisible) return;

    onShow?.();
  }, [isVisible, onShow]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={isVisible}
      size="small"
      accessibilityLabel={accessibilityLabel}
      onRequestClose={onRequestClose}
    >
      <CustomizationModalContentContainer size={size ?? match(['md', 'lg'])}>
        <HighlightWords
          text={heading}
          highlights={headingHighlights}
          sizeMatch={['22']}
        />

        <BodyText sizeMatch={['22']}>{body}</BodyText>

        <SafeAreaView edges={match([['bottom'], undefined])}>
          <HStack
            itemsPerRow={2}
            gap={theme.spacing['4']}
            style={styles.ctaContainer}
          >
            <Button
              size="large-wide"
              palette="primary"
              onPress={onAddMore}
              accessibilityLabel={formatMessage(messages.goBack)}
              style={ctaStyles}
            >
              <FormattedMessage {...messages.goBack} />
            </Button>

            <Button
              size="large-wide"
              palette="kale"
              onPress={onContinue}
              accessibilityLabel={formatMessage(messages.continue)}
              style={ctaStyles}
            >
              <FormattedMessage {...messages.continue} />
            </Button>
          </HStack>
        </SafeAreaView>
      </CustomizationModalContentContainer>
    </Modal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  modalLabel: {
    defaultMessage: 'Encouraged changes for {group}',
    description: 'Customization | Encourage Changes | Modal label',
  },
  heading: {
    defaultMessage: 'Heads up — only {count} portion of {group} selected.',
    description: 'Customization | Encourage Changes | Heading',
  },
  headingBases: {
    defaultMessage: 'Heads up - only {count} base portion selected.',
    description: 'Customization | Encourage Changes | Heading base',
  },
  headingNoSelection: {
    defaultMessage: 'Heads up — no {group} selected.',
    description: 'Customization | Encourage Changes | Heading no selection',
  },
  body: {
    defaultMessage: 'Are you sure you’d like to continue?',
    description: 'Customization | Encourage Changes | Body',
  },
  bodyBases: {
    defaultMessage:
      'You can get a full portion by adding another base. Are you sure you’d like to continue?',
    description: 'Customization | Encourage Changes | Body base',
  },
  base: {
    defaultMessage: 'base',
    description: 'Customization | Encourage Changes | Base',
  },
  continue: {
    defaultMessage: 'Continue',
    description: 'Customization | Continue',
  },
  goBack: {
    defaultMessage: 'Go back',
    description: 'Customization | Go back',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ctaContainer: {
    paddingTop: theme.spacing['2'],
    paddingBottom: Platform.select({
      android: theme.spacing['2'],
      default: 0,
    }),
  },
  ctaXS: {
    paddingHorizontal: theme.spacing['2'],
  },
  ctaLG: {
    minHeight: 72,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isVisible: boolean;
  entry: CustomizationMachineTypes.EncouragedChangeEntry;
  onAddMore: () => void;
  onContinue: () => void;
  onRequestClose?: () => void;
  onShow?: () => void;
  size?: 'md' | 'lg';
};
